import * as THREE from 'three';

// Builds the astronaut mesh tree.
// Local frame: +Y = up, +Z = forward (eyes / visor face +Z).
//
// Hierarchy:
//   character (Group)
//   ├── upperBody (Group, pivot at hip height — torso/head/arms/pack live
//   │              here so they can twist/bob/tilt as one piece)
//   └── legL, legR (on the main group — independently swing at the hip)
//
// The controller animates `legL`, `legR`, `armL`, `armR`, `torso`,
// `upperBody`, and `antennaTip`, exposed via `userData`.

const HIP_Y  = 0.45;   // pivot for upperBody (whole torso-and-up)
const NECK_Y = 1.08;   // pivot for headGroup (stabilises head vs. body)

export function makeCharacter() {
  const g = new THREE.Group();

  // ---------- Materials ----------
  const suitMat = new THREE.MeshStandardMaterial({ color: 0xeaecef, roughness: 0.6, metalness: 0.08 });
  const gearMat = new THREE.MeshStandardMaterial({ color: 0x2f3340, roughness: 0.85 });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x1a1d26, roughness: 0.9 });
  const visorMat = new THREE.MeshStandardMaterial({
    color: 0xc8a458, roughness: 0.18, metalness: 0.95,
    emissive: 0x2a1c08, emissiveIntensity: 0.25,
  });
  const glowR = new THREE.MeshStandardMaterial({ color: 0xee4a3a, emissive: 0xee4a3a, emissiveIntensity: 0.9 });
  const glowG = new THREE.MeshStandardMaterial({ color: 0x3ae870, emissive: 0x3ae870, emissiveIntensity: 0.9 });

  // ---------- Upper body group (everything waist-up pivots from the hip) ----
  const upperBody = new THREE.Group();
  upperBody.position.y = HIP_Y;
  g.add(upperBody);

  // Helper: add a mesh under `upperBody`, compensating the Y so its rest
  // world-position is unchanged after the hip offset.
  const addUpper = (mesh) => { mesh.position.y -= HIP_Y; upperBody.add(mesh); };

  // Head sub-group at the neck so we can counter-rotate/counter-bob the head
  // against the upper body. Everything helmet-and-above goes here.
  const headGroup = new THREE.Group();
  headGroup.position.y = NECK_Y - HIP_Y;  // in upperBody-local
  upperBody.add(headGroup);
  const addHead = (mesh) => { mesh.position.y -= NECK_Y; headGroup.add(mesh); };

  // ---------- Torso + belt ----------
  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.26, 0.46, 8, 16), suitMat);
  torso.position.y = 0.78;
  torso.castShadow = true;
  addUpper(torso);

  const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.275, 0.275, 0.07, 20), gearMat);
  belt.position.y = 0.58;
  belt.castShadow = true;
  addUpper(belt);

  // ---------- Life support (pack + tanks + hoses) ----------
  const pack = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.56, 0.2), gearMat);
  pack.position.set(0, 0.83, -0.26);
  pack.castShadow = true;
  addUpper(pack);

  const packSeam = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.025, 0.22), suitMat);
  packSeam.position.set(0, 0.83, -0.26);
  addUpper(packSeam);

  const tankGeo = new THREE.CapsuleGeometry(0.055, 0.38, 6, 12);
  const tankL = new THREE.Mesh(tankGeo, suitMat);
  tankL.position.set(-0.11, 0.84, -0.4);
  tankL.castShadow = true;
  addUpper(tankL);
  const tankR = new THREE.Mesh(tankGeo, suitMat);
  tankR.position.set(0.11, 0.84, -0.4);
  tankR.castShadow = true;
  addUpper(tankR);

  const hoseGeo = new THREE.TorusGeometry(0.06, 0.012, 6, 12, Math.PI);
  const hoseL = new THREE.Mesh(hoseGeo, darkMat);
  hoseL.position.set(-0.06, 1.07, -0.36);
  hoseL.rotation.z = Math.PI / 2;
  addUpper(hoseL);
  const hoseR = new THREE.Mesh(hoseGeo, darkMat);
  hoseR.position.set(0.06, 1.07, -0.36);
  hoseR.rotation.z = -Math.PI / 2;
  addUpper(hoseR);

  // ---------- Chest panel with glowing indicators ----------
  const panel = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.17, 0.03), gearMat);
  panel.position.set(0, 0.92, 0.265);
  panel.castShadow = true;
  addUpper(panel);
  const btnR = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.012), glowR);
  btnR.position.set(-0.07, 0.92, 0.28);
  addUpper(btnR);
  const btnG = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.012), glowG);
  btnG.position.set(0.06, 0.92, 0.28);
  addUpper(btnG);

  // ---------- Collar + helmet + visor + antenna ----------
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.22, 0.08, 20), gearMat);
  collar.position.y = 1.08;
  collar.castShadow = true;
  addUpper(collar);

  const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.23, 36, 28), suitMat);
  helmet.position.y = 1.25;
  helmet.castShadow = true;
  addHead(helmet);

  const visor = new THREE.Mesh(
    new THREE.SphereGeometry(0.232, 48, 32,
      Math.PI / 3, Math.PI / 3,
      Math.PI * 0.32, Math.PI * 0.36,
    ),
    visorMat,
  );
  visor.position.y = 1.25;
  addHead(visor);

  const antBase = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.028, 0.04, 12), gearMat);
  antBase.position.y = 1.49;
  addHead(antBase);
  const antRod = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.09, 6), darkMat);
  antRod.position.y = 1.55;
  addHead(antRod);

  const antennaTipMat = new THREE.MeshStandardMaterial({
    color: 0xff5a48, emissive: 0xff5a48, emissiveIntensity: 0.9,
  });
  const antTip = new THREE.Mesh(new THREE.SphereGeometry(0.022, 12, 8), antennaTipMat);
  antTip.position.y = 1.60;
  addHead(antTip);

  // ---------- Helmet lamp ----------
  // A small cylindrical housing with a glowing lens on the front of the
  // helmet, plus a SpotLight that points down-and-forward to illuminate
  // the ground ahead. No volumetric shaft — the lit ground plus the
  // emissive lens reads as "helmet torch" without the artifacts of a
  // visible cone mesh at odd camera angles.
  const LAMP_TILT = 0.55;                       // radians below horizontal (~31°)

  const lampHousingMat = new THREE.MeshStandardMaterial({
    color: 0x2a2d38, roughness: 0.5, metalness: 0.6, flatShading: true,
  });
  const lampHousing = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055, 0.065, 0.06, 16),
    lampHousingMat,
  );
  lampHousing.rotation.x = Math.PI / 2 + LAMP_TILT;
  lampHousing.position.set(0, 0.22, 0.2);
  lampHousing.castShadow = true;
  headGroup.add(lampHousing);

  const lampLensMat = new THREE.MeshStandardMaterial({
    color: 0xfff4c0, emissive: 0xfff4c0, emissiveIntensity: 1.1,
  });
  const lampLens = new THREE.Mesh(
    new THREE.CircleGeometry(0.048, 20), lampLensMat,
  );
  lampLens.rotation.x = LAMP_TILT;
  lampLens.position.set(0, 0.22, 0.233);
  headGroup.add(lampLens);

  const lamp = new THREE.SpotLight(
    0xfff4c0,    // warm-white
    10,          // intensity
    16,          // distance
    0.55,        // cone half-angle (~31°) — wider pool on ground
    0.5,         // softer penumbra
    1.1,         // decay
  );
  lamp.position.set(0, 0.22, 0.24);
  headGroup.add(lamp);

  // SpotLight target — placed along the tilt direction from the lamp.
  const tgtDist = 3;
  const lampTarget = new THREE.Object3D();
  lampTarget.position.set(
    0,
    0.22 - Math.sin(LAMP_TILT) * tgtDist,
    0.24 + Math.cos(LAMP_TILT) * tgtDist,
  );
  headGroup.add(lampTarget);
  lamp.target = lampTarget;

  // Console panel on TOP of the backpack, between the hose loops. Sits on a
  // natural flat surface, catches the camera's slight downward angle, and
  // doesn't get occluded by the tanks. Reads as the suit's primary control
  // unit rather than three loose lights.
  const consoleBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.02, 0.14),
    gearMat,
  );
  consoleBase.position.set(0, 1.12, -0.26);
  consoleBase.castShadow = true;
  addUpper(consoleBase);

  // Slightly inset lighter top panel — reads like a labeled display face.
  const consoleFace = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.01, 0.1),
    suitMat,
  );
  consoleFace.position.set(0, 1.135, -0.26);
  addUpper(consoleFace);

  // Three LEDs arranged in a row on top of the console face.
  const ledGeo = new THREE.SphereGeometry(0.015, 12, 10);
  const mkLED = (hex) => new THREE.Mesh(
    ledGeo,
    new THREE.MeshStandardMaterial({ color: hex, emissive: hex, emissiveIntensity: 0.85 }),
  );
  const backLedR = mkLED(0xff3a28);
  const backLedG = mkLED(0x3ef878);
  const backLedA = mkLED(0xffd820);    // punchy yellow — easier to read against the warm suit
  const ledY = 1.145;          // proud of the console face
  const ledZ = -0.26;
  backLedR.position.set(-0.055, ledY, ledZ);
  backLedG.position.set( 0.000, ledY, ledZ);
  backLedA.position.set( 0.055, ledY, ledZ);
  addUpper(backLedR);
  addUpper(backLedG);
  addUpper(backLedA);

  // ---------- Shoulders ----------
  const shoulderGeo = new THREE.SphereGeometry(0.18, 20, 14);
  const shoulderL = new THREE.Mesh(shoulderGeo, suitMat);
  shoulderL.position.set(-0.26, 1.04, 0);
  shoulderL.castShadow = true;
  addUpper(shoulderL);
  const shoulderR = new THREE.Mesh(shoulderGeo, suitMat);
  shoulderR.position.set(0.26, 1.04, 0);
  shoulderR.castShadow = true;
  addUpper(shoulderR);

  // ---------- Arms (rotate at the shoulder, parented to upperBody so they
  // inherit the twist) ----------
  const armGeo = new THREE.CapsuleGeometry(0.10, 0.32, 6, 12);
  armGeo.translate(0, -0.2, 0);
  const armL = new THREE.Mesh(armGeo, suitMat);
  armL.position.set(-0.33, 1.02, 0);
  armL.castShadow = true;
  addUpper(armL);
  const armR = new THREE.Mesh(armGeo, suitMat);
  armR.position.set(0.33, 1.02, 0);
  armR.castShadow = true;
  addUpper(armR);

  // Cuff + glove as children of the arms.
  const cuffGeo  = new THREE.CylinderGeometry(0.105, 0.125, 0.14, 24);
  const gloveGeo = new THREE.SphereGeometry(0.125, 20, 14);
  for (const arm of [armL, armR]) {
    const cuff = new THREE.Mesh(cuffGeo, darkMat);
    cuff.position.set(0, -0.39, 0);
    cuff.castShadow = true;
    arm.add(cuff);
    const glove = new THREE.Mesh(gloveGeo, darkMat);
    glove.position.set(0, -0.48, 0);
    glove.castShadow = true;
    arm.add(glove);
  }


  // ---------- Legs (parented to a hips group so the pelvis can counter-
  // rotate against the shoulder twist — the natural contra-motion of a
  // walking gait). Legs still swing independently at their own hip pivots. -
  const hips = new THREE.Group();
  g.add(hips);
  const legGeo = new THREE.CapsuleGeometry(0.13, 0.34, 6, 12);
  legGeo.translate(0, -0.22, 0); // pivot from top (hip)
  const legL = new THREE.Mesh(legGeo, suitMat);
  legL.position.set(-0.14, 0.45, 0);
  legL.castShadow = true;
  hips.add(legL);
  const legR = new THREE.Mesh(legGeo, suitMat);
  legR.position.set(0.14, 0.45, 0);
  legR.castShadow = true;
  hips.add(legR);

  const ankleGeo = new THREE.BoxGeometry(0.19, 0.11, 0.22);
  const soleGeo  = new THREE.BoxGeometry(0.22, 0.08, 0.32);
  for (const leg of [legL, legR]) {
    const ankle = new THREE.Mesh(ankleGeo, darkMat);
    ankle.position.set(0, -0.39, 0.03);
    ankle.castShadow = true;
    leg.add(ankle);
    const sole = new THREE.Mesh(soleGeo, darkMat);
    sole.position.set(0, -0.47, 0.06);
    sole.castShadow = true;
    leg.add(sole);
  }

  g.userData = {
    legL, legR, armL, armR,
    torso, upperBody, headGroup, hips,
    antennaTip: antTip,
    backLedR, backLedG, backLedA,
    lamp, lampLens,
    hipY: HIP_Y,
    headRestY: NECK_Y - HIP_Y,
  };
  return g;
}
